/**
 * transformAdapters — the bridge between the transform gizmo and the very
 * different ways this app stores an object's placement.
 *
 * The gizmo speaks one language: position / rotation / scale as plain arrays.
 * The objects don't. The environment splat is a real TRS in
 * liveTuneStore.environment.pose; the avatar is a live THREE.Group whose XZ is
 * also driven by the movement component; the spotlight (slice 3) is
 * distance/height/yaw on an orbit rig; the camera cage is half-extents with no
 * position of its own.
 *
 * So each selectable object gets an adapter:
 *
 *   id           stable key, also what sceneSelection stores
 *   label        outliner row text
 *   caps         which gizmo modes/axes make sense — see CAPS below
 *   note         optional one-liner shown in the outliner (e.g. "not saved")
 *   isAvailable  false → the object isn't in the scene right now
 *   read()       -> { position:[x,y,z], rotation:[x,y,z], scale:[x,y,z] }
 *   write(trs)   apply a gizmo edit back to wherever it actually lives
 *
 * read/write are the ONLY things that know an object's storage. Adding the
 * spotlight later means adding an adapter, not touching the gizmo.
 *
 * Rotation is radians throughout (matches what Scene.jsx feeds three).
 *
 * The factories take their dependencies so unit tests can drive them without a
 * browser; `getAdapters()` wires the real singletons.
 */

import { liveTuneStore } from './liveTuneStore.js'
import { movement } from './movementComponent.js'
import { normalizePose, posesEqual } from './envPose.js'
import { sphericalToXYZ, xyzToSpherical } from './lightPlacement.js'
import { resolveSplatAvatarRoot } from './avatarTransformTarget.js'
import { avatarFloor } from './avatarFloor.js'

/**
 * caps values, per mode:
 *   translate: 'xyz' | 'xz'  | false
 *   rotate:    'xyz' | 'y'   | false
 *   scale:     'xyz' | 'uniform' | false
 * A `false` mode is not offered in the outliner and the gizmo refuses it.
 */
export const AXES_BY_CAP = {
  xyz: { x: true, y: true, z: true },
  xz: { x: true, y: false, z: true },
  y: { x: false, y: true, z: false },
  // 'uniform' shows every handle and enforces uniformity in the WRITE instead
  // of by hiding handles. That is not a style choice — three's TransformControls
  // gates visibility with a SUBSTRING test:
  //
  //     handle.visible &&= ( handle.name.indexOf('X') === -1 || this.showX )   // …and Y, Z
  //
  // The centre uniform-scale handle is named 'XYZ', so it contains X, Y and Z.
  // Setting showX/showY/showZ all false to "leave only the centre box" hides
  // the centre box as well, and the gizmo renders nothing at all. There is no
  // supported way to isolate it (PlayCanvas has per-shape enable; three does
  // not), so every handle stays grabbable and collapseUniformScale() below
  // turns whichever one you drag into a single factor.
  uniform: { x: true, y: true, z: true },
}

/**
 * Collapse a possibly non-uniform scale triple to the single factor the store
 * can actually hold (default_pose.scale is one scalar).
 *
 * Picks the component furthest from `base`, which MUST be the scale at the
 * START of the drag — not the current stored value.
 *
 * Why that distinction matters: three recomputes the whole scale from a
 * drag-start snapshot on every move (`object.scale.copy(_scaleStart).multiply(…)`),
 * so the two axes you are NOT dragging stay pinned at the drag-start value for
 * the entire drag. If `base` tracked the live stored value instead, then as the
 * dragged axis pulls the store away, those two pinned axes would end up further
 * from `base` than the dragged one — and the pick would flip to them, snapping
 * the object back to where the drag started. A fixed base makes exactly one
 * component deviate, stably, for the whole gesture.
 */
export function collapseUniformScale(scale, base) {
  if (!Array.isArray(scale) || scale.length === 0) return base
  let best = base
  let bestDelta = -1
  for (const v of scale) {
    if (!Number.isFinite(v)) continue
    const d = Math.abs(v - base)
    if (d > bestDelta) { bestDelta = d; best = v }
  }
  return best
}

/** Which show{X,Y,Z} the gizmo should use for a given adapter + mode. */
export function axesFor(caps, mode) {
  const cap = caps?.[mode]
  if (!cap) return null
  return AXES_BY_CAP[cap] ?? AXES_BY_CAP.xyz
}

export function modeAllowed(caps, mode) {
  return Boolean(caps?.[mode])
}

// Guard against the scale-0 trap normalizePose already defends: a 0 scale
// renders the splat invisible with no error, which reads as "the environment
// broke". The gizmo can produce a 0 by dragging the centre box through the
// origin, so clamp here too.
const MIN_SCALE = 1e-4

// ──────────────────────────────────────────────────────────────────────
// Environment splat
// ──────────────────────────────────────────────────────────────────────

/**
 * @param store      liveTuneStore (or a fake, in tests)
 * @param getEnvRow  () => the server asset row for the ACTIVE environment, or
 *                   null. Supplies the saved default_pose that `dirty` compares
 *                   against, and the id that `save` PATCHes.
 * @param saveEnvPose (id, pose) => Promise. Persists to the server.
 */
export function createEnvironmentAdapter({ store, getEnvRow, saveEnvPose }) {
  return {
    id: 'environment',
    label: 'Environment',
    // Uniform only: a non-uniform splat scale has no sensible authoring
    // meaning here (default_pose stores a single `scale` scalar), and
    // supersplat makes the same call.
    caps: { translate: 'xyz', rotate: 'xyz', scale: 'uniform' },

    isAvailable: () => Boolean(store.getState().environment.splatUrl),

    /**
     * 'dirty' means the live pose differs from what the server has, i.e. this
     * edit exists only in this browser's localStorage and no teammate will see
     * it. Returns 'clean' when there is no server row to compare against —
     * a URL-param splat has nowhere to be saved, so claiming "unsaved" would be
     * telling the user to press a button that cannot help them.
     */
    dirty() {
      const row = getEnvRow?.()
      if (!row) return 'clean'
      const saved = row.defaultPose ?? row.default_pose ?? {}
      return posesEqual(saved, store.getState().environment.pose) ? 'clean' : 'dirty'
    },

    /** Present only when there is somewhere to save to — the UI hides the button otherwise. */
    canSave: () => Boolean(getEnvRow?.()?.id),

    async save() {
      const row = getEnvRow?.()
      if (!row?.id) throw new Error('no environment selected')
      await saveEnvPose(row.id, normalizePose(store.getState().environment.pose))
    },

    read() {
      const p = store.getState().environment.pose
      const s = Number.isFinite(p.scale) && p.scale > 0 ? p.scale : 1
      return {
        position: [p.px, p.py, p.pz],
        rotation: [p.rx, p.ry, p.rz],
        scale: [s, s, s],
      }
    },

    // `dragStart` is the transform read at the moment the drag began, supplied
    // by TransformGizmo. Required for a correct uniform collapse — see
    // collapseUniformScale. Absent (a programmatic write), the live value is a
    // fine stand-in because there is no in-flight gesture to be unstable across.
    write({ position, rotation, scale }, dragStart) {
      const pose = {}
      if (position) { [pose.px, pose.py, pose.pz] = position }
      if (rotation) { [pose.rx, pose.ry, pose.rz] = rotation }
      if (scale) {
        const startScale = dragStart?.scale?.[0]
        const cur = store.getState().environment.pose.scale
        const base = Number.isFinite(startScale) && startScale > 0
          ? startScale
          : (Number.isFinite(cur) && cur > 0 ? cur : 1)
        pose.scale = Math.max(MIN_SCALE, collapseUniformScale(scale, base))
      }
      store.setState({ environment: { pose } })
    },
  }
}

// ──────────────────────────────────────────────────────────────────────
// Avatar
// ──────────────────────────────────────────────────────────────────────

export function createAvatarAdapter({ getGroup, getMovement }) {
  return {
    id: 'avatar',
    label: 'Avatar',
    // Full XYZ translate + XYZ rotate + uniform scale — the PlayCanvas/Unreal
    // model, where a selected object accepts every axis unless something else
    // genuinely owns it.
    //   - Y position WAS refused because avatarFloor re-asserts
    //     group.position.y on every floor change, so a direct write vanished.
    //     The fix is composition, not refusal: the gizmo writes
    //     avatarFloor.setLift and the floor snap adds it, so both writers agree
    //     and a vertical drag survives an environment swap.
    //   - X/Z rotation WAS refused because movementComponent steers
    //     group.rotation.y while pathing. That conflict is already handled by
    //     the `moving` guard in write(), which refuses the whole transform —
    //     capping the axes on top of it just removed pitch and roll from a
    //     stationary character for no reason.
    //   - scale is uniform because a sheared rig is never what anyone wants.
    caps: { translate: 'xyz', rotate: 'xyz', scale: 'uniform' },
    note: 'Runtime only — not saved',

    isAvailable: () => Boolean(getGroup()),

    // Nothing to persist: the avatar's placement is transient runtime state,
    // which the row's `note` says out loud. Always-clean is the honest answer —
    // a dirty badge would imply a save that does not exist.
    dirty: () => 'clean',
    canSave: () => false,

    read() {
      const g = getGroup()
      if (!g) return { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] }
      return {
        position: [g.position.x, g.position.y, g.position.z],
        rotation: [g.rotation.x, g.rotation.y, g.rotation.z],
        // The REAL group scale, not a hardcoded 1: the inspector's Scale fields
        // and the gizmo both read this, so reporting a constant would show 1
        // while the avatar rendered at 2.
        scale: [g.scale.x, g.scale.y, g.scale.z],
      }
    },

    write({ position, rotation, scale }, dragStart) {
      const g = getGroup()
      if (!g) return
      // The movement component drives group.position.x/z every tick while
      // pathing. Writing underneath it would be silently undone next frame, so
      // refuse instead of pretending the edit landed.
      const moving = getMovement?.()?.status === 'moving'
      if (moving) return
      if (position) {
        g.position.x = position[0]
        g.position.z = position[2]
        // Y goes through the floor module, which then writes the group: floor
        // baseline + lift. Sending the DELTA (rather than an absolute lift)
        // means the caller never needs to know the rig's bbox offset, and the
        // result survives the next floor change.
        const dy = position[1] - g.position.y
        if (dy) avatarFloor.setLift(avatarFloor.getLift() + dy)
      }
      if (rotation) g.rotation.set(rotation[0], rotation[1], rotation[2])
      if (scale) {
        // Same collapse the environment adapter uses: whichever axis the pointer
        // pulled furthest from the drag-start baseline becomes the one factor.
        const startScale = dragStart?.scale?.[0]
        const base = Number.isFinite(startScale) && startScale > 0 ? startScale : (g.scale.x || 1)
        g.scale.setScalar(Math.max(MIN_SCALE, collapseUniformScale(scale, base)))
      }
    },
  }
}

// ──────────────────────────────────────────────────────────────────────
// Splat (OGS) avatar
// ──────────────────────────────────────────────────────────────────────

/**
 * A splat character has no rig group — OGSModel mounts SplatMeshes, not a
 * skinned GLB — so the GLB avatar adapter's target (__AVATAR_GROUP__) simply
 * doesn't exist and its row stayed unselectable. This drives the dedicated
 * `ogs-avatar-root` Group that OGSModel now parents every splat under.
 *
 * Caps match the GLB avatar: full XYZ translate + rotate, uniform scale. There
 * is no avatarFloor writer here, so Y is written straight onto the root; scale
 * stays uniform because a non-uniform splat scale would shear the gaussians.
 *
 * It carries the GLB adapter's `moving` guard because movement drives this root too:
 * a drag mid-path would be silently undone on the next tick.
 *
 * Only one of this and the GLB adapter is ever available — a character resolves
 * to exactly one render path.
 */
export function createSplatAvatarAdapter({ getRoot, getMovement }) {
  return {
    id: 'splatAvatar',
    label: 'Avatar',
    caps: { translate: 'xyz', rotate: 'xyz', scale: 'uniform' },
    note: 'Runtime only — not saved',

    isAvailable: () => Boolean(getRoot()),

    // No per-character pose field exists for splat avatars yet, so there is
    // nowhere to persist this. Always-clean is the honest answer; the row's
    // note says so.
    dirty: () => 'clean',
    canSave: () => false,

    read() {
      const g = getRoot()
      if (!g) return { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] }
      return {
        position: [g.position.x, g.position.y, g.position.z],
        rotation: [g.rotation.x, g.rotation.y, g.rotation.z],
        scale: [g.scale.x, g.scale.y, g.scale.z],
      }
    },

    write({ position, rotation, scale }, dragStart) {
      const g = getRoot()
      if (!g) return
      // Refuse rather than pretend the edit landed — movement owns x/z and yaw
      // while pathing.
      if (getMovement?.()?.status === 'moving') return
      if (position) g.position.set(position[0], position[1], position[2])
      if (rotation) g.rotation.set(rotation[0], rotation[1], rotation[2])
      if (scale) {
        const startScale = dragStart?.scale?.[0]
        const base = Number.isFinite(startScale) && startScale > 0
          ? startScale
          : (g.scale.x > 0 ? g.scale.x : 1)
        const s = Math.max(MIN_SCALE, collapseUniformScale(scale, base))
        g.scale.setScalar(s)
      }
      // The appearance decoder reads this matrix (via localFrameCamera) on the
      // next inference tick, so it has to be current before then rather than
      // waiting for R3F's own traversal.
      g.updateMatrixWorld(true)
    },
  }
}

// ──────────────────────────────────────────────────────────────────────
// Registry
// ──────────────────────────────────────────────────────────────────────
// Lights (ENG-1190: one adapter per positional light in liveTuneStore.lights)
// ──────────────────────────────────────────────────────────────────────

/**
 * A POINT or SPOT light from `liveTuneStore.lights[id]`. Dragging a light is
 * the whole point of having one you can place — you walk it past the subject
 * and watch the highlight follow — so each gets the standard gizmo rather than
 * one of its own.
 *
 * Storage stays in the light's own knobs, so the adapter is where cartesian
 * meets them: read places, write un-places. A point light is spherical
 * (distance/elevation/azimuth about [0, targetHeight, 0], lightPlacement.js);
 * a spot is cylindrical (distance/height/yaw about the Y axis — the orbit rig
 * SpotlightRig.jsx mounts). Either way there is ONE representation in the
 * store, and a drag lands in the very sliders the inspector shows instead of a
 * competing cartesian copy.
 *
 * Rotate and scale are false because neither light has them.
 */
export function createLightAdapter({ id, store, place = sphericalToXYZ, unplace = xyzToSpherical, getFloorY = defaultFloorY }) {
  const entry = () => store.getState().lights?.[id] ?? null
  // Write through setLight when the store has it (the real store — it refuses an
  // unknown id), else a plain setState (the unit tests' fake store).
  const patch = (knobs) => {
    if (typeof store.setLight === 'function') store.setLight(id, knobs)
    else store.setState({ lights: { [id]: knobs } })
  }
  // What each type orbits. A point light orbits its own aim height; the sun
  // orbits the head probe (head height above the stage floor — the same origin
  // SceneLights hands ShadowSunLight, approximated here from the store's floor
  // override or the detected env floor); a hemisphere "orbits" the world origin
  // at a fixed radius, since only its DIRECTION means anything.
  const origin = (l) => {
    if (l.type === 'point') return [0, l.targetHeight ?? 0, 0]
    if (l.type === 'directional') return [0, HEAD_HEIGHT + getFloorY(store), 0]
    return [0, 0, 0]
  }
  const HEMI_RADIUS = 2
  return {
    id,
    // Live: the row is renamed from the inspector, and the gizmo's header
    // should say what the outliner says.
    get label() { return entry()?.name ?? id },
    get caps() {
      const t = entry()?.type
      // Unreal's split: a point light has a position and nothing to rotate; a
      // spot has both; a directional is a direction (its "position" here is
      // where it sits on its orbit, so dragging it also aims it); the hemisphere
      // is a sky direction, rotate-only.
      if (t === 'point') return { translate: 'xyz', rotate: false, scale: false }
      if (t === 'spot') return { translate: 'xyz', rotate: 'xyz', scale: false }
      if (t === 'directional') return { translate: 'xyz', rotate: 'xyz', scale: false }
      if (t === 'hemisphere') return { translate: false, rotate: 'xyz', scale: false }
      return { translate: false, rotate: false, scale: false }
    },
    get note() {
      const t = entry()?.type
      return t === 'spot' ? 'Move = distance / height / yaw; rotate = aim'
        : t === 'point' ? 'Placement = distance / elevation / azimuth'
          : 'Rotate (or move) = elevation / azimuth'
    },
    // How big the object is for the F (frame) command — lights are small.
    frameRadius: 0.6,

    // A light that exists can be placed even while OFF (Unreal moves a hidden
    // actor too) — the one refusal is a point light in headlamp mode, where
    // TorchLight re-writes the position from the camera every frame and a drag
    // would be silently undone. Refusing the row beats pretending the edit
    // landed (same call as the avatar adapter's `moving` guard).
    isAvailable: () => {
      const l = entry()
      if (!l) return false
      if (l.type === 'point') return !l.followCamera
      return l.type in LIGHT_TYPE_CAPS
    },

    // The placement IS the store entry, and `lights` is in envTune.TUNE_SLICES —
    // so it persists through the scene tune, not through a per-object save
    // button here. Claiming otherwise would point the user at a button that
    // doesn't exist.
    dirty: () => 'clean',
    canSave: () => false,

    read() {
      const l = entry()
      const none = { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] }
      if (!l) return none
      switch (l.type) {
        case 'point':
          return { position: place(l.distance, l.elevation, l.azimuth, origin(l)), rotation: [0, 0, 0], scale: [1, 1, 1] }
        case 'spot': {
          const position = cylindricalToXYZ(l.distance, l.height, l.yaw)
          // Aimed freely by a rotate drag, else derived from the axis target it
          // looks at — so the rotate rings always sit where the beam points.
          const rotation = hasAim(l) ? [l.aimX, l.aimY, l.aimZ] : lookAtEuler(position, [0, l.targetHeight ?? 0, 0])
          return { position, rotation, scale: [1, 1, 1] }
        }
        case 'directional': {
          const o = origin(l)
          const position = place(l.distance, l.elevation, l.azimuth, o)
          return { position, rotation: lookAtEuler(position, o), scale: [1, 1, 1] }
        }
        case 'hemisphere': {
          const position = place(HEMI_RADIUS, l.elevation ?? 90, l.azimuth ?? 0, [0, 0, 0])
          return { position, rotation: lookAtEuler(position, [0, 0, 0]), scale: [1, 1, 1] }
        }
        default:
          return none
      }
    },

    // Returns the knobs it stored, or null when it refused — the gizmo ignores
    // this, but it's what makes __TORCH__.setPos able to say "that didn't land"
    // instead of reporting a placement the headlamp overwrites next frame.
    //
    // The gizmo writes position AND rotation on every change, whichever handle
    // is being dragged, so which one MOVED is decided against `dragStart` (the
    // transform read when the gesture began): a translate drag must not pin a
    // spot's aim to its old derived rotation, and a rotate drag must not
    // re-place a directional from its unchanged position.
    write({ position, rotation }, dragStart) {
      if (!this.isAvailable()) return null
      const l = entry()
      const rotated = !!rotation && (!dragStart?.rotation || !nearlyEqual(rotation, dragStart.rotation))
      const moved = !!position && (!dragStart?.position || !nearlyEqual(position, dragStart.position))
      let knobs = null
      switch (l.type) {
        case 'point':
          if (!position) return null
          knobs = unplace(position, origin(l))
          break
        case 'spot': {
          knobs = {}
          if (position && (moved || !rotation)) Object.assign(knobs, xyzToCylindrical(position))
          if (rotated) { knobs.aimX = rotation[0]; knobs.aimY = rotation[1]; knobs.aimZ = rotation[2] }
          if (!Object.keys(knobs).length) return null
          break
        }
        case 'directional':
        case 'hemisphere': {
          const o = origin(l)
          const radius = l.type === 'hemisphere' ? HEMI_RADIUS : l.distance
          if (rotated) {
            // The light points along its forward; put it back on its orbit at
            // the spot that forward, aimed at the origin, implies.
            const f = forwardOf(rotation)
            const p = [o[0] - f[0] * radius, o[1] - f[1] * radius, o[2] - f[2] * radius]
            const s = unplace(p, o)
            knobs = { elevation: s.elevation, azimuth: s.azimuth }
          } else if (position && moved && l.type === 'directional') {
            // The sun can also be dragged around its orbit; the hemisphere is
            // a direction only (caps say so) and ignores a move.
            const s = unplace(position, o)
            knobs = { distance: s.distance, elevation: s.elevation, azimuth: s.azimuth }
          } else return null
          break
        }
        default:
          return null
      }
      patch(knobs)
      return knobs
    },
  }
}

const LIGHT_TYPE_CAPS = { point: 1, spot: 1, directional: 1, hemisphere: 1 }
// AVATAR_HEAD_PROBE in Scene.jsx — the height the sun orbits above the stage floor.
const HEAD_HEIGHT = 1.4

// The stage floor as Scene.jsx resolves it: the pose's explicit floorY override,
// else the splat-detected floor the environment published, else 0.
function defaultFloorY(store) {
  const fy = store.getState()?.environment?.pose?.floorY
  if (Number.isFinite(fy)) return fy
  if (typeof window !== 'undefined' && Number.isFinite(window.__ENV_FLOOR_Y__)) return window.__ENV_FLOOR_Y__
  return 0
}

function hasAim(l) {
  return Number.isFinite(l.aimX) && Number.isFinite(l.aimY) && Number.isFinite(l.aimZ)
}

function nearlyEqual(a, b, eps = 1e-9) {
  return a.length === b.length && a.every((v, i) => Math.abs(v - b[i]) < eps)
}

/**
 * The Euler (XYZ, radians — what TransformControls' proxy carries) of an
 * object at `from` looking at `to`, with three's LIGHT convention: lights and
 * cameras look down their local -Z. Pure (no THREE import, so `node --test` can
 * drive it) — the maths is three's Matrix4.lookAt + Euler.setFromRotationMatrix.
 */
export function lookAtEuler(from, to, up = [0, 1, 0]) {
  let z = [from[0] - to[0], from[1] - to[1], from[2] - to[2]]
  let len = Math.hypot(...z)
  if (len < 1e-12) z = [0, 0, 1]; else z = z.map(v => v / len)
  let x = cross(up, z)
  len = Math.hypot(...x)
  if (len < 1e-12) {
    // Looking straight along `up`: nudge z, as three's Matrix4.lookAt does.
    if (Math.abs(up[2]) === 1) z[0] += 1e-4; else z[2] += 1e-4
    len = Math.hypot(...z); z = z.map(v => v / len)
    x = cross(up, z); len = Math.hypot(...x)
  }
  x = x.map(v => v / len)
  const y = cross(z, x)
  // Columns x, y, z of the rotation matrix → XYZ Euler, exactly three's
  // Euler.setFromRotationMatrix for order 'XYZ' (m13 = z.x, m23 = z.y,
  // m33 = z.z, m12 = y.x, m11 = x.x, m32 = y.z, m22 = y.y).
  const m13 = z[0]
  const ey = Math.asin(Math.max(-1, Math.min(1, m13)))
  let ex, ez
  if (Math.abs(m13) < 0.9999999) {
    ex = Math.atan2(-z[1], z[2])
    ez = Math.atan2(-y[0], x[0])
  } else {
    ex = Math.atan2(y[2], y[1])
    ez = 0
  }
  return [ex, ey, ez]
}

/** Where an object with XYZ Euler `rotation` (radians) points: its local -Z in
 *  world — three's light/camera forward. For order 'XYZ' the matrix's third
 *  column is (sin y, -sin x cos y, cos x cos y); forward is its negation, and
 *  the Z rotation (roll about the forward axis) does not move it. */
export function forwardOf([ex, ey]) {
  const cx = Math.cos(ex), sx = Math.sin(ex), cy = Math.cos(ey), sy = Math.sin(ey)
  return [-sy, sx * cy, -cx * cy]
}

function cross(a, b) {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}

// The spot rig's own convention: a group at the origin yaws about Y, the light
// sits at local [0, height, distance]. Kept here rather than in lightPlacement
// because it is a different frame from the spherical one — it orbits a vertical
// AXIS, not a point — and the two must not be mistaken for each other.
export function cylindricalToXYZ(distance, height, yawDeg) {
  const d = Math.max(0, distance)
  const yaw = (yawDeg * Math.PI) / 180
  return [d * Math.sin(yaw), height, d * Math.cos(yaw)]
}

export function xyzToCylindrical([x, y, z]) {
  const distance = Math.hypot(x, z)
  return {
    distance,
    height: y,
    yaw: distance < 1e-9 ? 0 : (Math.atan2(x, z) * 180) / Math.PI,
  }
}

// ──────────────────────────────────────────────────────────────────────

let _adapters = null
// One adapter per positional light, cached by id so the gizmo's `adapter`
// identity is stable across renders (TransformGizmo re-seeds its proxy when the
// adapter changes). Built lazily as lights appear; a removed light's adapter
// just stops being listed.
const _lightAdapters = new Map()

// Resolve the server row for whatever environment is currently active.
// Reads CustomAssetManager's in-memory list rather than fetching — the list is
// already loaded and kept fresh by CAM, and a second fetch here would be a
// second source of truth for the same rows. Imported lazily to keep this module
// node-testable (CAM pulls in browser-only dependencies).
function activeEnvRow() {
  if (typeof window === 'undefined') return null
  const rows = window.__ENVIRONMENTS_LIST__
  if (!Array.isArray(rows)) return null
  const url = liveTuneStore.getState().environment.splatUrl
  if (!url) return null
  return rows.find(r => (r.fileUrl ?? r.file_url) === url) ?? null
}

/** All adapters, real singletons wired in. Order = outliner order. */
export function getAdapters() {
  if (!_adapters) {
    _adapters = [
      createEnvironmentAdapter({
        store: liveTuneStore,
        getEnvRow: activeEnvRow,
        saveEnvPose: async (id, pose) => {
          const mod = await import('./CustomAssetManager.js')
          await mod.CustomAssetManager.updateEnvironmentAsset(id, { defaultPose: pose })
        },
      }),
      createAvatarAdapter({
        getGroup: () => (typeof window !== 'undefined' && window.__AVATAR_GROUP__) || null,
        getMovement: () => movement,
      }),
      // Splat characters take this path instead; exactly one of the two is
      // available for any given character.
      createSplatAvatarAdapter({
        getRoot: () => resolveSplatAvatarRoot(),
        getMovement: () => movement,
      }),
    ]
  }
  // The lights are objects (ENG-1190): every light the store holds right now
  // gets its adapter — a transform is how Unreal and PlayCanvas expose a light,
  // and the outliner should not list an object you cannot grab. Which handles
  // each type offers is the adapter's `caps`.
  const lights = liveTuneStore.getState().lights ?? {}
  const out = [..._adapters]
  for (const id of Object.keys(lights)) {
    if (!lights[id]?.type) continue
    let a = _lightAdapters.get(id)
    if (!a) {
      a = createLightAdapter({ id, store: liveTuneStore })
      _lightAdapters.set(id, a)
    }
    out.push(a)
  }
  return out
}

export function getAdapter(id) {
  if (!id) return null
  return getAdapters().find(a => a.id === id) ?? null
}
